<script setup>
import { ref, onMounted, computed, watch } from 'vue';

const loading = ref(true);
const error = ref(null);
const maintenanceSettings = ref({
  title: 'Website wird gewartet',
  message: 'Wir arbeiten derzeit an Verbesserungen unserer Website. Bitte schauen Sie später wieder vorbei.',
  show_countdown: false,
  end_date: null,
  background_image: null,
  custom_content: null,
  social_links: false,
  show_contact: false
});

const countdown = ref({
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
});

const countdownInterval = ref(null);

// SiteSettings abrufen (für Kontakt und Social Media)
const siteSettings = ref({
  facebook_url: '',
  instagram_url: '',
  youtube_url: '',
  tiktok_url: '',
  contact_email: '',
  contact_phone: '',
  contact_location: ''
});

// Rechtliche Inhalte
const imprintContent = ref('');
const privacyContent = ref('');

// Modal-Status
const showImprintModal = ref(false);
const showPrivacyModal = ref(false);

// Hintergrundbild-Stil
const backgroundStyle = computed(() => {
  if (maintenanceSettings.value.background_image) {
    return {
      backgroundImage: `url(${maintenanceSettings.value.background_image})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  }
  return {};
});

// Countdown berechnen
const updateCountdown = () => {
  if (!maintenanceSettings.value.end_date) {
    return;
  }
  
  const endDate = new Date(maintenanceSettings.value.end_date);
  const now = new Date();
  
  // Wenn das Enddatum in der Vergangenheit liegt, stoppt der Countdown
  if (endDate <= now) {
    countdown.value = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value);
      countdownInterval.value = null;
    }
    return;
  }
  
  const diff = endDate - now;
  
  // Tage, Stunden, Minuten und Sekunden berechnen
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  countdown.value = { days, hours, minutes, seconds };
};

// Impressum laden
const loadImprint = async () => {
  try {
    const response = await fetch('/api/imprint.php');
    const data = await response.json();
    
    if (data.success && data.imprint) {
      imprintContent.value = data.imprint.content || 'Impressum-Inhalte konnten nicht geladen werden.';
    } else {
      // Fallback-Inhalt
      imprintContent.value = `
        <h2>Impressum</h2>
        <p><strong>Angaben gemäß § 5 TMG:</strong></p>
        <p>HomeWard<br />
        Irish Folk Band</p>
        <p><strong>Kontakt:</strong><br />
        E-Mail: ${siteSettings.value.contact_email || 'info@homeward-music.com'}</p>
      `;
    }
  } catch (err) {
    console.error('Fehler beim Laden des Impressums:', err);
    // Fallback-Inhalt
    imprintContent.value = `
      <h2>Impressum</h2>
      <p><strong>Angaben gemäß § 5 TMG:</strong></p>
      <p>HomeWard<br />
      Irish Folk Band</p>
      <p><strong>Kontakt:</strong><br />
      E-Mail: ${siteSettings.value.contact_email || 'info@homeward-music.com'}</p>
    `;
  }
};

// Datenschutz laden
const loadPrivacy = async () => {
  try {
    const response = await fetch('/api/privacy.php');
    const data = await response.json();
    
    if (data.success && data.privacy) {
      privacyContent.value = data.privacy.content || 'Datenschutz-Inhalte konnten nicht geladen werden.';
    } else {
      // Fallback-Inhalt
      privacyContent.value = `
        <h2>Datenschutzerklärung</h2>
        <p>Wir freuen uns über Ihr Interesse an unserer Website. Der Schutz Ihrer Privatsphäre ist für uns sehr wichtig.</p>
        
        <h3>Verantwortliche Stelle</h3>
        <p>Verantwortliche Stelle im Sinne der Datenschutzgesetze ist:</p>
        <p>HomeWard Irish Folk Band<br />
        E-Mail: ${siteSettings.value.contact_email || 'info@homeward-music.com'}</p>
        
        <h3>Ihre Rechte</h3>
        <p>Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.</p>
        
        <h3>Datenerfassung auf dieser Website</h3>
        <p><strong>Cookies</strong></p>
        <p>Unsere Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die auf Ihrem Endgerät abgelegt werden. Ihr Browser greift auf diese Dateien zu.</p>
        
        <p><strong>Server-Log-Dateien</strong></p>
        <p>Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:</p>
        <ul>
          <li>Browsertyp und -version</li>
          <li>Verwendetes Betriebssystem</li>
          <li>Referrer URL</li>
          <li>Hostname des zugreifenden Rechners</li>
          <li>Uhrzeit der Serveranfrage</li>
          <li>IP-Adresse</li>
        </ul>
        
        <h3>Kontaktformular</h3>
        <p>Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
      `;
    }
  } catch (err) {
    console.error('Fehler beim Laden der Datenschutzerklärung:', err);
    // Fallback-Inhalt
    privacyContent.value = `
      <h2>Datenschutzerklärung</h2>
      <p>Wir freuen uns über Ihr Interesse an unserer Website. Der Schutz Ihrer Privatsphäre ist für uns sehr wichtig.</p>
      
      <h3>Verantwortliche Stelle</h3>
      <p>Verantwortliche Stelle im Sinne der Datenschutzgesetze ist:</p>
      <p>HomeWard Irish Folk Band<br />
      E-Mail: ${siteSettings.value.contact_email || 'info@homeward-music.com'}</p>
      
      <h3>Ihre Rechte</h3>
      <p>Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.</p>
      
      <h3>Datenerfassung auf dieser Website</h3>
      <p><strong>Cookies</strong></p>
      <p>Unsere Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die auf Ihrem Endgerät abgelegt werden. Ihr Browser greift auf diese Dateien zu.</p>
      
      <p><strong>Server-Log-Dateien</strong></p>
      <p>Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:</p>
      <ul>
        <li>Browsertyp und -version</li>
        <li>Verwendetes Betriebssystem</li>
        <li>Referrer URL</li>
        <li>Hostname des zugreifenden Rechners</li>
        <li>Uhrzeit der Serveranfrage</li>
        <li>IP-Adresse</li>
      </ul>
      
      <h3>Kontaktformular</h3>
      <p>Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
    `;
  }
};

// Impressum anzeigen
const openImprintModal = () => {
  if (!imprintContent.value) {
    loadImprint();
  }
  showImprintModal.value = true;
};

// Datenschutz anzeigen
const openPrivacyModal = () => {
  if (!privacyContent.value) {
    loadPrivacy();
  }
  showPrivacyModal.value = true;
};

// Modals schließen
const closeModals = () => {
  showImprintModal.value = false;
  showPrivacyModal.value = false;
};

// Hintergrund-Scrolling verhindern bei geöffnetem Modal
const preventScroll = (show) => {
  if (show) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

// Watcher für Modal-Status
watch(showImprintModal, preventScroll);
watch(showPrivacyModal, preventScroll);

// Daten laden
const loadData = async () => {
  try {
    loading.value = true;
    
    // Wartungseinstellungen laden
    const maintenanceResponse = await fetch('/api/maintenance_settings.php');
    const maintenanceData = await maintenanceResponse.json();
    
    if (maintenanceData.success && maintenanceData.settings) {
      maintenanceSettings.value = maintenanceData.settings;
    }
    
    // Site-Settings laden (für Social Media und Kontakt)
    const siteResponse = await fetch('/api/settings.php');
    const siteData = await siteResponse.json();
    
    if (siteData.success && siteData.settings) {
      siteSettings.value = siteData.settings;
    }
    
    // Countdown initialisieren, wenn aktiviert
    if (maintenanceSettings.value.show_countdown && maintenanceSettings.value.end_date) {
      updateCountdown();
      countdownInterval.value = setInterval(updateCountdown, 1000);
    }
    
  } catch (err) {
    console.error('Fehler beim Laden der Wartungs-Daten:', err);
    error.value = 'Die Wartungsseite konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

// Format-Funktionen für den Countdown
const formatNumber = (num) => {
  return num.toString().padStart(2, '0');
};

// Komponente bereinigen
const cleanupComponent = () => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }
  document.body.style.overflow = ''; // Scrolling wiederherstellen, falls nötig
};

// Beim Aufbau der Komponente
onMounted(() => {
  loadData();
  
  // Cleanup beim Abbau der Komponente
  return cleanupComponent;
});
</script>

<template>
  <div class="maintenance-page" :style="backgroundStyle">
    <div class="maintenance-overlay"></div>
    
    <div class="maintenance-container">
      <div v-if="loading" class="maintenance-loading">
        <div class="spinner"></div>
        <p>Wird geladen...</p>
      </div>
      
      <div v-else-if="error" class="maintenance-error">
        <h2>Fehler</h2>
        <p>{{ error }}</p>
      </div>
      
      <div v-else>
        <!-- Benutzerdefinierter Inhalt, falls vorhanden -->
        <div v-if="maintenanceSettings.custom_content" v-html="maintenanceSettings.custom_content" class="custom-content"></div>
        
        <!-- Standard-Wartungsseite, wenn kein benutzerdefinierter Inhalt vorhanden ist -->
        <div v-else class="maintenance-content">
          <div class="logo">
        <div class="logo-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
          </svg>
        </div>
            <span>HomeWard</span>
          </div>
          
          <h1>{{ maintenanceSettings.title }}</h1>
          <p class="message">{{ maintenanceSettings.message }}</p>
          
          <!-- Countdown-Anzeige -->
          <div v-if="maintenanceSettings.show_countdown" class="countdown">
            <div class="countdown-item">
              <span class="countdown-number">{{ formatNumber(countdown.days) }}</span>
              <span class="countdown-label">Tage</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">{{ formatNumber(countdown.hours) }}</span>
              <span class="countdown-label">Stunden</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">{{ formatNumber(countdown.minutes) }}</span>
              <span class="countdown-label">Minuten</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">{{ formatNumber(countdown.seconds) }}</span>
              <span class="countdown-label">Sekunden</span>
            </div>
      </div>
      
          <!-- Social-Media-Links -->
          <div v-if="maintenanceSettings.social_links" class="social-links">
            <a v-if="siteSettings.facebook_url" :href="siteSettings.facebook_url" target="_blank" class="social-link">
              <i class="fab fa-facebook-f"></i>
            </a>
            <a v-if="siteSettings.instagram_url" :href="siteSettings.instagram_url" target="_blank" class="social-link">
              <i class="fab fa-instagram"></i>
            </a>
            <a v-if="siteSettings.youtube_url" :href="siteSettings.youtube_url" target="_blank" class="social-link">
              <i class="fab fa-youtube"></i>
            </a>
            <a v-if="siteSettings.tiktok_url" :href="siteSettings.tiktok_url" target="_blank" class="social-link">
              <i class="fab fa-tiktok"></i>
            </a>
          </div>
          
          <!-- Kontaktinformationen -->
          <div v-if="maintenanceSettings.show_contact" class="contact-info">
            <div v-if="siteSettings.contact_email" class="contact-item">
              <i class="fas fa-envelope"></i>
              <span>{{ siteSettings.contact_email }}</span>
            </div>
            <div v-if="siteSettings.contact_phone" class="contact-item">
              <i class="fas fa-phone"></i>
              <span>{{ siteSettings.contact_phone }}</span>
            </div>
            <div v-if="siteSettings.contact_location" class="contact-item">
              <i class="fas fa-map-marker-alt"></i>
              <span>{{ siteSettings.contact_location }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Rechtliche Links mit Modal-Öffner (immer anzeigen) -->
      <div class="legal-links">
        <a href="/impressum?bypass=true" target="_blank">Impressum</a>
        <span class="divider">|</span>
        <a href="/datenschutz?bypass=true" target="_blank">Datenschutz</a>
      </div>
    </div>
    
    <!-- Impressum Modal -->
    <div v-if="showImprintModal" class="modal-overlay" @click="showImprintModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Impressum</h2>
          <button class="modal-close" @click="showImprintModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="!imprintContent" class="modal-loading">
            <div class="spinner"></div>
            <p>Inhalt wird geladen...</p>
          </div>
          <div v-else v-html="imprintContent"></div>
        </div>
        </div>
      </div>
      
    <!-- Datenschutz Modal -->
    <div v-if="showPrivacyModal" class="modal-overlay" @click="showPrivacyModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Datenschutz</h2>
          <button class="modal-close" @click="showPrivacyModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="!privacyContent" class="modal-loading">
            <div class="spinner"></div>
            <p>Inhalt wird geladen...</p>
          </div>
          <div v-else v-html="privacyContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.maintenance-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-color);
  padding: 2rem;
  overflow: hidden;
}

.maintenance-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1;
}

.maintenance-container {
  position: relative;
  max-width: 800px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 3rem 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  z-index: 2;
  text-align: center;
}

.maintenance-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.maintenance-error {
  color: #842029;
  padding: 1rem;
}

.maintenance-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 2rem;
}

.logo-icon {
  height: 40px;
  width: 40px;
  margin-right: 0.5rem;
  color: var(--primary-color);
}

h1 {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.message {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-color);
  max-width: 600px;
  margin-bottom: 2rem;
}

.countdown {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 2rem 0;
}

.countdown-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-number {
  background-color: var(--primary-color);
  color: white;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1.5rem;
  font-weight: 700;
  min-width: 60px;
  text-align: center;
}

.countdown-label {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-muted);
}

.social-links {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  transition: all 0.3s ease;
}

.social-link:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.contact-info {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: var(--text-color);
}

.contact-item i {
  color: var(--primary-color);
}

/* Rechtliche Links */
.legal-links {
  margin-top: 2rem;
  font-size: 0.9rem;
  color: var(--text-muted);
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.legal-links a {
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s ease;
  cursor: pointer;
}

.legal-links a:hover {
  color: var(--primary-color);
  text-decoration: underline;
  }

.divider {
  margin: 0 0.75rem;
  opacity: 0.5;
}

/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--primary-color);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.2s ease;
  padding: 0.25rem 0.5rem;
  line-height: 1;
}

.modal-close:hover {
  color: var(--primary-color);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 60px);
  text-align: left;
  line-height: 1.6;
}

.modal-body h1, .modal-body h2, .modal-body h3 {
  color: var(--primary-color);
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

/* Responsives Design */
@media (max-width: 768px) {
  .maintenance-container {
    padding: 2rem 1rem;
  }
  
  h1 {
    font-size: 2rem;
  }
  
  .message {
    font-size: 1rem;
  }
  
  .countdown {
    gap: 0.5rem;
  }
  
  .countdown-number {
    font-size: 1.25rem;
    min-width: 50px;
    padding: 0.5rem;
  }
  
  .modal-content {
    max-height: 95vh;
  }
  
  .modal-body {
    max-height: calc(95vh - 60px);
  }
}

@media (max-width: 480px) {
  .countdown {
    flex-wrap: wrap;
  }
  
  .countdown-item {
    flex-basis: 40%;
    margin-bottom: 1rem;
  }
}
</style> 